/**
 * Integration and sync health (KAN-45, `integrations.manage`).
 *
 * The reason this screen exists before any dashboard does: a figure drawn from a
 * source that last synced a week ago is worse than a missing figure, because it
 * looks current. Everything here answers "how stale is this, and did the last
 * attempt actually work".
 */
import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  SYNC_SOURCES,
  describeCron,
  type IntegrationStatus,
  type SyncResource,
  type SyncRunStatus,
  type SyncSource,
  type SyncTrigger,
  isCompanyScoped,
} from '@accounting/shared'
import { ApiError, apiFetch } from '../lib/api'
import { formatCount, formatDateTime, formatRelative, timezoneLabel } from '../lib/format'
import { Alert } from '../ui/Alert'
import { Badge, type Tone } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { PageHeader } from '../ui/PageHeader'
import { Progress, type ProgressTone } from '../ui/Progress'
import { EmptyRow, TableWrap, Td, Th } from '../ui/Table'
import { SyncIcon } from '../ui/icons'

/**
 * Which connection an action is aimed at.
 *
 * The source alone stopped being an address when the firm connected its second
 * QuickBooks company: two connections share it, and the realm is what tells
 * them apart. `company` is null for a source that has never been connected and
 * for one whose account was never captured — the server takes both as the same
 * unnamed row.
 */
interface ConnectionRef {
  source: SyncSource
  company: string | null
}

interface Connection {
  source: SyncSource
  status: IntegrationStatus
  /** The QuickBooks company, and the half of this row's identity that is not the source. */
  externalAccountId: string | null
  connectedAt: string | null
  expiresAt: string | null
  lastError: string | null
  oauth: {
    /** Whether this source has an OAuth handshake in this build at all. */
    available: boolean
    /** Whether its client id and secret are present in the deployment. */
    configured: boolean
    /** The callback URL the provider must have registered, byte for byte. */
    redirectUri: string | null
  }
}

/**
 * How the round trip ended, as the callback reports it.
 *
 * Wording lives here rather than travelling in the URL: the outcome is a word,
 * and a query string ends up in browser history and in every proxy log between
 * Clio and the user.
 */
const RESULTS: Record<string, { tone: 'success' | 'warning' | 'error'; message: string }> = {
  connected: {
    tone: 'success',
    message: 'Connected. The next scheduled sync will use it — or press "Sync now" on a resource.',
  },
  declined: {
    tone: 'warning',
    message: 'Authorisation was declined, so nothing changed. Press Connect to try again.',
  },
  unverified: {
    tone: 'error',
    message:
      'That authorisation link had expired or had already been used, so it was refused. Press Connect to start again.',
  },
  exchange_failed: {
    tone: 'error',
    message:
      'The provider refused the exchange. The reason is on the connection card below as the last error — a redirect URL that does not match the registration is the usual cause.',
  },
  /**
   * QuickBooks only, and nothing was written: the firm runs two companies, so a
   * connection that cannot say which one it is would be read as either.
   */
  no_company: {
    tone: 'error',
    message:
      'QuickBooks came back without naming a company, so the connection was refused and nothing changed. Start again from the QuickBooks company you mean to connect.',
  },
}

interface LastRun {
  id: string
  status: SyncRunStatus
  trigger: SyncTrigger
  startedAt: string
  finishedAt: string | null
  recordsRead: number
  recordsWritten: number
  recordsFailed: number
  /** What the upstream said was in scope. Null when it does not say. */
  recordsTotal: number | null
  /** Last heartbeat from the worker; the counts above are as of this moment. */
  progressAt: string | null
  attempt: number
  errorMessage: string | null
  /** Server's verdict: `running`, but the worker has stopped reporting. */
  stalled: boolean
}

interface Schedule {
  /** Standard 5-field cron expression. */
  cron: string
  /** IANA zone the expression is evaluated in — the firm's, not the viewer's. */
  timezone: string
}

interface ResourceStatus {
  resource: SyncResource
  /**
   * Which upstream company the row is about — a QuickBooks realm, or empty for
   * a source that has no companies.
   *
   * The firm runs two QuickBooks companies, each syncing on its own, so a
   * resource can appear more than once. The pair is the identity of a row.
   */
  sourceCompany: string
  schedule: Schedule | null
  lastRun: LastRun | null
  lastSuccessAt: string | null
  /** Records covered by the last completed run — a sense of scale, not a total. */
  previousRecords: number | null
}

const SOURCE_LABELS: Record<SyncSource, string> = {
  clio: 'Clio',
  qbo: 'QuickBooks Online',
}

const CONNECTION_TONES: Record<IntegrationStatus, Tone> = {
  connected: 'positive',
  expired: 'warning',
  error: 'critical',
  disconnected: 'neutral',
}

const RUN_TONES: Record<SyncRunStatus, Tone> = {
  succeeded: 'positive',
  running: 'brand',
  partial: 'warning',
  failed: 'critical',
}

const PROGRESS_TONES: Record<SyncRunStatus, ProgressTone> = {
  succeeded: 'positive',
  running: 'brand',
  partial: 'warning',
  failed: 'critical',
}

/**
 * How often to re-read sync status while something is in flight.
 *
 * Matched to the worker's heartbeat rather than chosen for smoothness: polling
 * faster than the counts are written buys identical responses.
 */
const POLL_MS = 2_000

/**
 * How long to keep polling after a manual trigger before giving up on seeing it
 * start.
 *
 * A queued job has no run row until a worker picks it up, so for a few seconds
 * after pressing "Sync now" there is nothing running to poll *for* — without
 * this the screen would go quiet at exactly the moment somebody is watching it.
 * A minute is long enough to cover a busy queue and short enough that a worker
 * which is not running stops being polled about.
 */
const AWAIT_START_MS = 60_000

export function Integrations() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  /** Set by the OAuth callback redirecting the browser back here. */
  const result = searchParams.get('result')
  const resultSource = searchParams.get('source')
  const outcome = result ? RESULTS[result] : undefined

  const integrations = useQuery({
    queryKey: ['sync-integrations'],
    queryFn: () => apiFetch<{ integrations: Connection[] }>('/sync/integrations'),
  })

  /**
   * Set when a manual sync is queued, so the poll below keeps going during the
   * gap between the job being accepted and a worker creating its run row.
   */
  const [queuedAt, setQueuedAt] = useState<number | null>(null)

  const status = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => apiFetch<{ resources: ResourceStatus[] }>('/sync/status'),
    /**
     * Polled only while there is something to watch. A stalled run is excluded
     * deliberately: its counts are frozen by definition, so polling it would
     * refetch an unchanging answer indefinitely.
     */
    refetchInterval: ({ state }) => {
      const running = (state.data?.resources ?? []).some(
        (entry) => entry.lastRun?.status === 'running' && !entry.lastRun.stalled
      )
      const awaitingStart = queuedAt !== null && Date.now() - queuedAt < AWAIT_START_MS

      return running || awaitingStart ? POLL_MS : false
    },
  })

  const trigger = useMutation({
    mutationFn: (resource: SyncResource) =>
      apiFetch<{ jobId: string; resource: SyncResource }>('/sync/trigger', {
        method: 'POST',
        body: JSON.stringify({ resource }),
      }),
    onSuccess: () => {
      setQueuedAt(Date.now())
      return queryClient.invalidateQueries({ queryKey: ['sync-status'] })
    },
  })

  /**
   * Starting the handshake is a request; following it is a navigation. The
   * server mints a one-time state and returns where to send the browser,
   * because a navigation cannot carry the in-memory bearer token that a fetch
   * can.
   */
  const connect = useMutation({
    mutationFn: (source: SyncSource) =>
      apiFetch<{ authorizeUrl: string; redirectUri: string }>(
        `/sync/integrations/${source}/authorize`,
        { method: 'POST' }
      ),
    onSuccess: ({ authorizeUrl }) => {
      window.location.href = authorizeUrl
    },
  })

  /**
   * Addressed by company, not just by source.
   *
   * The server refuses an unnamed disconnect when a source has more than one
   * connection, and it is right to — with both QuickBooks companies stored, a
   * bare `DELETE /integrations/qbo` would have taken down an arbitrary one of
   * them. The card knows which row it is drawn from, so it names it.
   */
  const disconnect = useMutation({
    mutationFn: ({ source, company }: ConnectionRef) =>
      apiFetch<{ source: SyncSource; externalAccountId: string | null }>(
        `/sync/integrations/${source}?company=${encodeURIComponent(company ?? '')}`,
        { method: 'DELETE' }
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sync-integrations'] }),
  })

  const connections = integrations.data?.integrations ?? []
  const resources = status.data?.resources ?? []

  return (
    <>
      <PageHeader
        title="Integrations"
        description="Connection state for each upstream system, and the outcome of the last sync per resource."
        actions={
          <Button
            onClick={() => {
              void queryClient.invalidateQueries({ queryKey: ['sync-status'] })
              void queryClient.invalidateQueries({ queryKey: ['sync-integrations'] })
            }}
          >
            <SyncIcon />
            Refresh
          </Button>
        }
      />

      {outcome && (
        <Alert tone={outcome.tone} className="mb-6">
          <span>
            {resultSource && <strong>{SOURCE_LABELS[resultSource as SyncSource] ?? resultSource}: </strong>}
            {outcome.message}
          </span>{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              const next = new URLSearchParams(searchParams)
              next.delete('result')
              next.delete('source')
              setSearchParams(next, { replace: true })
            }}
          >
            Dismiss
          </button>
        </Alert>
      )}

      {(trigger.error || connect.error || disconnect.error) && (
        <Alert tone="error" className="mb-6">
          {messageFor(trigger.error ?? connect.error ?? disconnect.error)}
        </Alert>
      )}

      {trigger.isSuccess && (
        <Alert tone="success" className="mb-6">
          Queued a sync of <strong>{trigger.data.resource}</strong>. It runs on the worker; the row
          below updates itself while it does — no need to press Refresh. (Reloading the page would
          sign you out: the token is held in memory, never in browser storage.)
        </Alert>
      )}

      {/*
        * One card per connection rather than per source. The firm runs two
        * QuickBooks companies, and the list used to be built by finding the
        * first connection matching each source — which showed one of the two
        * and gave no sign the other was there.
        *
        * With no rows to draw — still loading, or the request failed — the
        * sources stand in for them. Keyed off the rows themselves rather than
        * off `isPending`, because the failed case needs it too: these cards are
        * what carry the error message, and a screen that renders none of them
        * would report a failure by going blank.
        */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        {(connections.length > 0
          ? connections.map((connection) => ({ source: connection.source, connection }))
          : SYNC_SOURCES.map((source) => ({ source, connection: undefined }))
        ).map(({ source, connection }) => {
          const company = connection?.externalAccountId ?? null
          const ref: ConnectionRef = { source, company }

          return (
            <ConnectionCard
              key={`${source} ${company ?? ''}`}
              source={source}
              connection={connection}
              /**
               * Whether this source has a second company on screen, which is
               * what decides if the card needs to name its own. Naming one
               * beside a source that has a single connection would be noise.
               */
              companyIsAmbiguous={
                connections.filter((entry) => entry.source === source).length > 1
              }
              loading={integrations.isPending}
              error={integrations.error ? messageFor(integrations.error) : null}
              busy={
                (connect.isPending && connect.variables === source) ||
                (disconnect.isPending &&
                  disconnect.variables?.source === source &&
                  disconnect.variables?.company === company)
              }
              onConnect={() => connect.mutate(source)}
              onDisconnect={() => disconnect.mutate(ref)}
            />
          )
        })}

        {/*
          * An explicit way to add a company, for sources that have them.
          *
          * Adding one was already possible without this — the handshake keys
          * its row on whichever company is chosen at the provider, so pressing
          * Reconnect and picking a different one has always added rather than
          * replaced. What was missing was any way to know that: a lone card
          * offering "Reconnect" and "Disconnect" reads as one connection being
          * all a source gets, which is exactly the wrong conclusion for a firm
          * that keeps its trust and operating books apart.
          */}
        {addableSources(connections).map((source) => (
          <Card key={`add ${source}`}>
            <CardHeader title={`Add a ${SOURCE_LABELS[source]} company`} />
            <CardBody className="text-sm">
              <p className="text-xs leading-relaxed text-ink-600">
                The firm keeps more than one set of {SOURCE_LABELS[source]} books — trust and
                operating — and each is connected separately. Authorising a company that is not
                already listed above adds it; it does not replace anything.
              </p>
              <div className="mt-3">
                <Button
                  size="sm"
                  variant="accent"
                  disabled={connect.isPending}
                  onClick={() => connect.mutate(source)}
                >
                  Add a company
                </Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader
          title="Sync resources"
          description="Each resource syncs independently, so a failure on one does not roll back another."
        />

        {status.error ? (
          <CardBody>
            <Alert tone="error">{messageFor(status.error)}</Alert>
          </CardBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Resource</Th>
                <Th>Schedule</Th>
                <Th>Last run</Th>
                <Th className="w-56">Progress</Th>
                <Th>Last success</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {status.isPending && <EmptyRow colSpan={6}>Loading sync status…</EmptyRow>}

              {!status.isPending && resources.length === 0 && (
                <EmptyRow colSpan={6}>No sync handlers are registered.</EmptyRow>
              )}

              {resources.map((entry) => (
                <tr key={`${entry.resource} ${entry.sourceCompany}`}>
                  <Td>
                    <code className="font-mono text-ink-900">{entry.resource}</code>
                    {/*
                      * Shown only when the resource has more than one row, which
                      * is only ever QuickBooks with both companies connected.
                      * Naming a company beside a resource that has just one
                      * would be noise on every Clio row.
                      */}
                    {companyIsAmbiguous(resources, entry) && (
                      <div className="mt-0.5 font-mono text-xs text-ink-500">
                        company {entry.sourceCompany}
                      </div>
                    )}
                  </Td>

                  <Td className="whitespace-nowrap">
                    <ScheduleCell schedule={entry.schedule} />
                  </Td>

                  <Td>
                    {entry.lastRun ? (
                      <div className="flex flex-col gap-1">
                        <span className="flex items-center gap-2">
                          <Badge
                            tone={entry.lastRun.stalled ? 'critical' : RUN_TONES[entry.lastRun.status]}
                          >
                            {entry.lastRun.stalled ? 'stalled' : entry.lastRun.status}
                          </Badge>
                          <span className="text-xs text-ink-600">{entry.lastRun.trigger}</span>
                        </span>
                        <span className="text-xs text-ink-600">
                          {formatRelative(entry.lastRun.finishedAt ?? entry.lastRun.startedAt)}
                          {entry.lastRun.attempt > 1 && ` · attempt ${entry.lastRun.attempt}`}
                        </span>
                        {entry.lastRun.errorMessage && (
                          <span className="max-w-72 text-xs break-words text-red-800">
                            {entry.lastRun.errorMessage}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-ink-500">never run</span>
                    )}
                  </Td>

                  <Td className="tabular">
                    <ProgressCell entry={entry} />
                  </Td>

                  <Td className="whitespace-nowrap">
                    <span className="block text-ink-800">{formatRelative(entry.lastSuccessAt)}</span>
                    {entry.lastSuccessAt && (
                      <span className="block text-xs text-ink-500">
                        {formatDateTime(entry.lastSuccessAt)}
                      </span>
                    )}
                  </Td>

                  <Td className="text-right">
                    <Button
                      size="sm"
                      variant="accent"
                      disabled={trigger.isPending && trigger.variables === entry.resource}
                      onClick={() => trigger.mutate(entry.resource)}
                    >
                      Sync now
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      <p className="mt-4 max-w-3xl text-xs leading-relaxed text-ink-600">
        A run is recorded before the work starts, so a job that dies hard still leaves evidence —
        the difference between "the sync failed" and "the sync never ran". Retries use exponential
        backoff, and the resume cursor only advances past records that committed, so a mid-run
        failure re-reads that window rather than skipping it.
      </p>
    </>
  )
}

/**
 * The sources worth offering an extra company for.
 *
 * Only sources that *have* companies, and only once at least one is connected
 * and working: before that the plain Connect button on the card above is the
 * thing to press, and two ways to start the same first handshake is a choice
 * nobody needs to make. Gated on `oauth.configured` too — a deployment without
 * client credentials cannot complete any handshake, so offering a second one
 * would be a button whose only outcome is an error.
 */
function addableSources(connections: Connection[]): SyncSource[] {
  return SYNC_SOURCES.filter(
    (source) =>
      isCompanyScoped(source) &&
      connections.some(
        (entry) =>
          entry.source === source && entry.oauth.configured && entry.status !== 'disconnected'
      )
  )
}

/**
 * Whether this row needs its company named to be told from its siblings.
 *
 * True only when the same resource appears more than once, which happens when
 * both QuickBooks companies are connected. Until then the table looks exactly
 * as it did.
 */
function companyIsAmbiguous(rows: ResourceStatus[], row: ResourceStatus): boolean {
  return rows.filter((other) => other.resource === row.resource).length > 1
}

/**
 * When this resource next runs, as a sentence.
 *
 * The cron expression is what the scheduler is configured with, but nobody
 * reads a column of `45 3 * * *` and comes away knowing when anything happens —
 * the point of this column is the answer, not the configuration. The expression
 * stays reachable as the cell's tooltip, because on the one screen where a
 * schedule is being questioned, the exact string is what gets pasted into a
 * ticket.
 *
 * A cadence too irregular to phrase falls back to the raw cron rather than an
 * approximation: this screen exists to say how much to trust a figure, so a
 * confident sentence that is slightly wrong about when data arrives would
 * undercut the whole thing.
 */
function ScheduleCell({ schedule }: { schedule: Schedule | null }) {
  if (!schedule) {
    return <span className="text-ink-500">on demand</span>
  }

  const described = describeCron(schedule.cron)

  if (!described) {
    return <code className="font-mono text-xs text-ink-700">{schedule.cron}</code>
  }

  const zone = timezoneLabel(schedule.timezone)

  return (
    <span className="block text-ink-800" title={`${schedule.cron} (${schedule.timezone})`}>
      {described}
      {zone && <span className="text-ink-500"> {zone}</span>}
    </span>
  )
}

/**
 * How far the last run got, live while it is still going.
 *
 * Three cases, and the difference between them is the whole design:
 *
 * - The upstream reported a total, so there is a real percentage to show.
 * - It did not, and the run is in flight — an unbounded count next to a moving
 *   bar. The count is the honest figure; the bar only says "still working".
 * - It did not, and the run is over — the counts alone, with no bar. A finished
 *   run must never show a sweeping bar; that reads as "still going".
 *
 * The alternative to all this was measuring today's count against last night's
 * total, which produces a confident percentage that is wrong whenever the
 * window differs — on the one screen whose entire job is saying how much to
 * trust a figure. Last night's count is still shown, as history, labelled as
 * such.
 */
function ProgressCell({ entry }: { entry: ResourceStatus }) {
  const run = entry.lastRun

  if (!run) {
    return <span className="text-ink-500">—</span>
  }

  const running = run.status === 'running' && !run.stalled
  const total = run.recordsTotal
  const measurable = typeof total === 'number' && total > 0
  const percent = measurable ? Math.min(100, Math.round((run.recordsRead / total) * 100)) : null

  /**
   * A stalled run keeps its bar only if it can be drawn to scale — a frozen
   * sweep animation is not available, and a static indeterminate bar says
   * nothing the "stalled" badge has not already said.
   */
  const showBar = measurable || running

  return (
    <div className="flex flex-col gap-1.5">
      {showBar && (
        <Progress
          value={run.recordsRead}
          max={measurable ? total : null}
          tone={run.stalled ? 'critical' : PROGRESS_TONES[run.status]}
          label={`${entry.resource} sync progress`}
        />
      )}

      <span className="text-xs text-ink-700">
        {run.status === 'running' ? (
          measurable ? (
            <>
              {formatCount(run.recordsRead)} of {formatCount(total)} · {percent}%
            </>
          ) : (
            <>
              {formatCount(run.recordsRead)} read ·{' '}
              {entry.previousRecords !== null ? (
                <span className="text-ink-600">{formatCount(entry.previousRecords)} last time</span>
              ) : (
                <span className="text-ink-600">size not reported</span>
              )}
            </>
          )
        ) : (
          <>
            {formatCount(run.recordsRead)} read · {formatCount(run.recordsWritten)} written
          </>
        )}

        {run.recordsFailed > 0 && (
          <span className="text-red-800"> · {formatCount(run.recordsFailed)} failed</span>
        )}
      </span>

      {run.stalled && (
        <span className="text-xs text-red-800">
          No progress since {formatRelative(run.progressAt ?? run.startedAt)} — the worker looks
          gone. Press Sync now to start again.
        </span>
      )}
    </div>
  )
}

function ConnectionCard({
  source,
  connection,
  companyIsAmbiguous,
  loading,
  error,
  busy,
  onConnect,
  onDisconnect,
}: {
  source: SyncSource
  connection: Connection | undefined
  companyIsAmbiguous: boolean
  loading: boolean
  error: string | null
  busy: boolean
  onConnect: () => void
  onDisconnect: () => void
}) {
  /**
   * No row means never connected, which is a state worth naming rather than
   * leaving as an empty card.
   */
  const status: IntegrationStatus = connection?.status ?? 'disconnected'
  const oauth = connection?.oauth
  const connected = status === 'connected'

  return (
    <Card>
      <CardHeader
        title={SOURCE_LABELS[source]}
        /**
         * The company sits in the header only when it is what tells this card
         * from the one beside it. Two cards headed "QuickBooks Online" with the
         * realm buried in the body is two cards nobody can act on.
         */
        description={
          companyIsAmbiguous
            ? `Company ${connection?.externalAccountId ?? '(never captured)'}`
            : undefined
        }
        actions={
          loading ? (
            <Badge tone="neutral">Checking…</Badge>
          ) : (
            <Badge tone={CONNECTION_TONES[status]}>{status}</Badge>
          )
        }
      />
      <CardBody className="text-sm">
        {error ? (
          <Alert tone="error">{error}</Alert>
        ) : (
          <dl className="space-y-1.5 text-ink-700">
            <Row label="Account">
              {connection?.externalAccountId ? (
                <code className="font-mono text-xs">{connection.externalAccountId}</code>
              ) : (
                'not linked'
              )}
            </Row>
            <Row label="Connected">{formatDateTime(connection?.connectedAt ?? null, 'never')}</Row>
            <Row label="Token expires">
              {connection?.expiresAt ? formatRelative(connection.expiresAt) : '—'}
            </Row>
            {connection?.lastError && (
              <Row label="Last error">
                <span className="text-red-800">{connection.lastError}</span>
              </Row>
            )}
          </dl>
        )}

        {!error && oauth && (
          <div className="mt-4 border-t border-ink-100 pt-3">
            {!oauth.available ? (
              <p className="text-xs leading-relaxed text-ink-600">
                No OAuth handshake for this source yet — it needs an app registration this firm does
                not hold. See <code className="font-mono">docs/decisions.md</code>.
              </p>
            ) : !oauth.configured ? (
              <p className="text-xs leading-relaxed text-ink-600">
                Client credentials are not present in this deployment, so the handshake cannot be
                started.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="accent" disabled={busy} onClick={onConnect}>
                    {connected ? 'Reconnect' : 'Connect'}
                  </Button>
                  {connection && status !== 'disconnected' && (
                    <Button size="sm" disabled={busy} onClick={onDisconnect}>
                      Disconnect
                    </Button>
                  )}
                </div>

                <p className="mt-2 text-xs leading-relaxed text-ink-600">
                  {SOURCE_LABELS[source]} will ask you to authorise, then send you back here. You
                  will be asked to sign in again on the way back — the session token is held in
                  memory, never in browser storage.
                </p>

                {/*
                  * Says what the button does for a source that has companies,
                  * because "Reconnect" does not: which row the handshake lands
                  * on is decided by the company picked at the provider, not by
                  * the card it was started from. Somebody adding the firm's
                  * operating book should not have to infer that from the label.
                  */}
                {isCompanyScoped(source) && (
                  <p className="mt-2 text-xs leading-relaxed text-ink-600">
                    Whichever company you choose at {SOURCE_LABELS[source]} is the one this
                    applies to. Choosing a different one <strong>adds</strong> it as a second
                    connection and leaves this one alone; only the same company reconnects this
                    card.
                  </p>
                )}

                {oauth.redirectUri && (
                  <p className="mt-2 text-xs leading-relaxed text-ink-600">
                    Registered callback must be exactly{' '}
                    <code className="font-mono break-all text-ink-800">{oauth.redirectUri}</code>.
                    {' '}A mismatch is refused by {SOURCE_LABELS[source]}, not by us.
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <dt className="text-ink-600">{label}</dt>
      <dd className="text-right text-ink-900">{children}</dd>
    </div>
  )
}

function messageFor(error: unknown): string {
  return error instanceof ApiError
    ? `${error.message} (${error.status})`
    : 'Something went wrong. Try again.'
}
